// Compact elapsed-time labels for board cards ("6d 12h", "12m 04s", "45s"). Pure functions:
// no React, no locale (unit letters are the same in en and pt-BR), unit-tested in test/.

const UNITS = [
  { suffix: "d", ms: 86_400_000 },
  { suffix: "h", ms: 3_600_000 },
  { suffix: "m", ms: 60_000 },
  { suffix: "s", ms: 1_000 },
] as const;

export interface CompactDurationOptions {
  /** How many units to show, starting from the largest non-zero one (default 2). */
  units?: 1 | 2;
}

/**
 * Largest units of a duration: 45s, 12m 04s, 3h 05m, 6d 12h. The second unit is kept even when
 * zero ("12m 00s") so a ticking timer does not change shape; minutes and seconds are zero-padded.
 * Returns null for unknown values (NaN, negative clock skew beyond a second) — never "0s" for unknown.
 */
export function formatCompactDuration(ms: number, options: CompactDurationOptions = {}): string | null {
  if (!Number.isFinite(ms)) return null;
  if (ms < 0) {
    // A client clock slightly behind the server must not render a negative age.
    if (ms < -1_000) return null;
    ms = 0;
  }
  const units = options.units ?? 2;
  let rest = Math.floor(ms / 1000) * 1000;
  const first = UNITS.findIndex((u) => rest >= u.ms);
  const start = first === -1 ? UNITS.length - 1 : first;
  const parts: string[] = [];
  for (let i = start; i < UNITS.length && parts.length < units; i += 1) {
    const unit = UNITS[i]!;
    const value = Math.floor(rest / unit.ms);
    rest -= value * unit.ms;
    const padded = parts.length > 0 && (unit.suffix === "m" || unit.suffix === "s") ? String(value).padStart(2, "0") : String(value);
    parts.push(`${padded}${unit.suffix}`);
  }
  return parts.join(" ");
}

/** Elapsed time between an ISO timestamp and `now` (ms since epoch); null when the timestamp is unknown. */
export function elapsedSince(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null;
  const start = Date.parse(iso);
  return Number.isNaN(start) ? null : now - start;
}

/** Priority score label: scores in [0,1] with 3 decimals ("0.250"); larger legacy scores as integers. */
export function formatScore(score: number): string {
  return Math.abs(score) <= 1 ? score.toFixed(3) : String(Math.round(score));
}
